import { sleep } from "@/lib/utils";

import type {
	Api,
	ApplicationsSummary,
	DashboardCard,
	DashboardOverview,
	DashboardRange,
	DashboardSection,
	Metric,
	SeriesPoint,
} from "../types";
import { PLATFORM_START_DATE } from "../types";
import { applications_api } from "./applications";

// Deterministic pseudo-random so the dashboard doesn't reshuffle on every
// render — same range always yields the same picture, which is what you want
// when demoing and when eyeballing a UI change.
const rand = (seed: number) => {
	const x = Math.sin(seed) * 10_000;
	return x - Math.floor(x);
};

const DAY_MS = 86_400_000;

/**
 * Application SLA in days — kept in step with APPLICATION_SLA_DAYS on the
 * applications page. Overdue counts here and the auto-priority there must
 * never disagree, or the dashboard accuses a manager the board says is fine.
 */
const MANAGER_SLA_DAYS = 3;

/**
 * Length of the selected window in days. Custom ranges are inclusive of both
 * ends, and clamped to at least one day so a same-day period still renders.
 */
export const rangeDays = (range: DashboardRange): number => {
	if (range.kind === "allTime") {
		const start = new Date(PLATFORM_START_DATE).getTime();
		if (Number.isNaN(start)) return 30;
		return Math.max(1, Math.round((Date.now() - start) / DAY_MS) + 1);
	}
	if (range.kind === "preset") return range.days;
	const from = new Date(range.from).getTime();
	const to = new Date(range.to).getTime();
	if (Number.isNaN(from) || Number.isNaN(to)) return 30;
	return Math.max(1, Math.round((to - from) / DAY_MS) + 1);
};

/** Last day of the window — today for presets and all-time, `to` for custom. */
const rangeEnd = (range: DashboardRange): Date =>
	range.kind === "custom" ? new Date(range.to) : new Date();

/** A series that trends upward with weekday seasonality and a little noise. */
const series = (range: DashboardRange, seed: number, base: number, growth: number): SeriesPoint[] => {
	const days = rangeDays(range);
	const end = rangeEnd(range);
	// Long custom periods (a year) would otherwise emit 365 points; sample them
	// down so the chart stays readable and the payload stays small.
	const step = days > 120 ? Math.ceil(days / 90) : 1;
	const points: SeriesPoint[] = [];
	for (let i = 0; i < days; i += step) {
		const date = new Date(end);
		date.setDate(end.getDate() - (days - 1 - i));
		// Weekends dip ~30% — makes the chart read like real marketplace traffic.
		const weekend = date.getDay() === 0 || date.getDay() === 6 ? 0.7 : 1;
		const trend = base * (1 + (growth * i) / days);
		const noise = 0.8 + rand(seed + i) * 0.4;
		points.push({
			date: date.toISOString().slice(0, 10),
			value: Math.max(0, Math.round(trend * weekend * noise * step)),
		});
	}
	return points;
};

const total = (points: SeriesPoint[]) => points.reduce((sum, p) => sum + p.value, 0);

/** Compare the window against the equally-sized window before it. */
const metric = (points: SeriesPoint[], previousTotal: number): Metric => {
	const value = total(points);
	if (previousTotal <= 0) return { value };
	return { value, delta: (value - previousTotal) / previousTotal };
};

const card = (
	key: string,
	href: string,
	range: DashboardRange,
	seed: number,
	base: number,
	growth: number,
	/** All-time population this card's period figure adds to. */
	cumulative?: number,
): DashboardCard => {
	const points = series(range, seed, base, growth);
	// Synthesise the prior window from the same generator, shifted by the seed.
	const previous = total(series(range, seed + 500, base, growth * 0.5));
	return { key, href, series: points, metric: metric(points, previous), total: cumulative };
};

/**
 * A metric that is a rate or an average rather than a sum — averaging the
 * series is the honest reduction, since totalling "average session length"
 * would be meaningless.
 */
const averaged = (
	key: string,
	href: string,
	range: DashboardRange,
	seed: number,
	base: number,
	growth: number,
): DashboardCard => {
	const points = series(range, seed, base, growth);
	const mean = Math.round(total(points) / Math.max(1, points.length));
	const previousPoints = series(range, seed + 500, base, growth * 0.5);
	const previousMean = Math.round(total(previousPoints) / Math.max(1, previousPoints.length));
	return {
		key,
		href,
		series: points,
		metric: previousMean > 0 ? { value: mean, delta: (mean - previousMean) / previousMean } : { value: mean },
	};
};

/** Applications counters, derived from the same store the board reads. */
const applicationsSummary = async (): Promise<ApplicationsSummary> => {
	const board = await applications_api.board();
	const statuses = new Map<string, number>();
	let danger = 0;
	let warning = 0;

	for (const application of board.applications) {
		statuses.set(application.status, (statuses.get(application.status) ?? 0) + 1);
		if (["active", "rejected", "lost"].includes(application.status)) continue;

		const days = Math.floor((Date.now() - new Date(application.statusChangedAt).getTime()) / DAY_MS);
		// Mirrors the thresholds the board itself uses.
		if (days >= 7 || (!application.assignee && application.priority === "high")) danger += 1;
		else if (days >= 4 || !application.assignee) warning += 1;
	}

	return {
		statuses: [...statuses.entries()].map(([status, count]) => ({
			status: status as ApplicationsSummary["statuses"][number]["status"],
			count,
		})),
		alerts: [
			{ tone: "danger", count: danger },
			{ tone: "warning", count: warning },
		],
	};
};

// Returns leaderboard. Modelled on the seller panel's "Топ 5 товаров с высокими
// возвратами": the count alone is useless without the dominant reason next to
// it, because the reason is what you act on.
const TOP_RETURNS: [string, string, number, string][] = [
	["Керамическая ваза «Север»", "Арт. 884213 · Керамика", 34, "damaged"],
	["Льняное постельное бельё", "Арт. 771904 · Текстиль", 28, "notAsDescribed"],
	["Серебряные серьги «Капля»", "Арт. 550118 · Украшения", 21, "sizeMismatch"],
	["Деревянная разделочная доска", "Арт. 330742 · Декор", 17, "qualityBelowExpected"],
	["Шерстяной плед", "Арт. 219556 · Текстиль", 12, "changedMind"],
];

// Returns aggregated to the SELLER, which the product leaderboard above cannot
// show. A shop with ten mediocre SKUs never puts a single one in the product
// top, yet it is the worst partner on the platform — «у него в топ выбился один
// товар, а у него 10 товаров возвратные, и суммарно эти 10 товаров…».
// [shop, slug, returns, return rate %, dominant reason]
const TOP_RETURN_SHOPS: [string, string, number, number, string][] = [
	["Уютный Дом", "uyutny-dom", 96, 14, "damaged"],
	["Handmade Studio", "handmade-studio", 71, 11, "notAsDescribed"],
	["Керамика Плюс", "keramika-plus", 58, 9, "damaged"],
	["ArtCraft", "artcraft", 44, 7, "sizeMismatch"],
	["Эко Мастерская", "eco-workshop", 31, 5, "qualityBelowExpected"],
];

// The other half of "недоброкачественный партнёр": returns say what came back,
// reviews say what people wrote. The founder judges a seller on both — «люди
// возвращают, им не нравится… люди пишут негативные отзывы… это говорит о нас
// тоже плохо, в двух местах».
// [shop, slug, reviews counted, average rating]
const TOP_REVIEWS_POSITIVE: [string, string, number, number][] = [
	["Silver Moon", "silver-moon", 412, 4.9],
	["НатурТекстиль", "naturtextil", 388, 4.8],
	["Гончарная мастерская", "goncharnaya", 341, 4.8],
	["Мастерская Анны", "masterskaya-anny", 294, 4.7],
	["Дерево и Смола", "derevo-smola", 236, 4.7],
];

const TOP_REVIEWS_NEGATIVE: [string, string, number, number][] = [
	["Уютный Дом", "uyutny-dom", 187, 2.4],
	["Handmade Studio", "handmade-studio", 154, 2.8],
	["Керамика Плюс", "keramika-plus", 121, 3.1],
	["ArtCraft", "artcraft", 96, 3.3],
	["Эко Мастерская", "eco-workshop", 74, 3.5],
];

// "Топ 5 товаров с задержкой выполнения", re-aimed at shops: at platform level
// the actionable unit is the seller who is late, not the SKU.
const TOP_DELAYS: [string, string, number, number][] = [
	["Уютный Дом", "uyutny-dom", 14, 9],
	["ArtCraft", "artcraft", 11, 7],
	["Handmade Studio", "handmade-studio", 8, 6],
	["Эко Мастерская", "eco-workshop", 6, 5],
	["Керамика Плюс", "keramika-plus", 4, 4],
];

/**
 * Manager workload, derived from the same board the applications page reads —
 * so "Мария не обработала" says the same thing in both places. Applications
 * are only one of a manager's duties, but they are the one we can measure.
 */
const managerWorkload = async (): Promise<DashboardSection> => {
	const board = await applications_api.board();
	const open = board.applications.filter(
		(entry) => !["active", "rejected", "lost"].includes(entry.status),
	);

	type Bucket = {
		name: string;
		total: number;
		overdue: number;
		worst?: string;
		worstDays: number;
		/** Days summed across the manager's open cards — divided out below. */
		daysSum: number;
	};
	const buckets = new Map<string, Bucket>();

	for (const application of open) {
		const id = application.assignee?.id ?? "unassigned";
		const name = application.assignee?.name ?? "Без ответственного";
		const bucket = buckets.get(id) ?? { name, total: 0, overdue: 0, worstDays: 0, daysSum: 0 };
		const days = Math.floor((Date.now() - new Date(application.statusChangedAt).getTime()) / DAY_MS);

		bucket.total += 1;
		bucket.daysSum += days;
		// Overdue is the SLA, not a magic number: same threshold the applications
		// page uses to raise priority.
		if (days >= MANAGER_SLA_DAYS) bucket.overdue += 1;
		if (days > bucket.worstDays) {
			bucket.worstDays = days;
			bucket.worst = `${application.shopName} · ${days} дн.`;
		}
		buckets.set(id, bucket);
	}

	/** Mean days an application waits — the "кто не работает" number. */
	const avgDays = (bucket: Bucket) =>
		bucket.total === 0 ? 0 : Math.round((bucket.daysSum / bucket.total) * 10) / 10;

	const named = [...buckets.entries()].filter(([id]) => id !== "unassigned");
	const platformAvg =
		open.length === 0
			? 0
			: Math.round(
					([...buckets.values()].reduce((sum, b) => sum + b.daysSum, 0) / open.length) * 10,
				) / 10;

	// Unowned work first, then the most overdue: the order you'd triage in.
	const ranked = [...buckets.entries()].sort(([idA, a], [idB, b]) => {
		if (idA === "unassigned") return -1;
		if (idB === "unassigned") return 1;
		return b.overdue - a.overdue || b.total - a.total;
	});

	// Headline counters the founder asked for by name: «общее количество
	// менеджеров я должна видеть», and Айталы's «средний срок обработки заявки —
	// можно добавить, чтобы посмотреть, кто не работает».
	const flat = (key: string, href: string, value: number): DashboardCard => ({
		key,
		href,
		metric: { value },
		series: [],
	});

	return {
		cards: [
			flat("managersTotal", "/platform-roles", named.length),
			flat("managersOpenApplications", "/applications", open.length),
			flat("managersAvgDays", "/applications", platformAvg),
			flat(
				"managersOverdue",
				"/applications",
				[...buckets.values()].reduce((sum, b) => sum + b.overdue, 0),
			),
		],
		blocks: [
			{
				key: "managerLoad",
				href: "/applications",
				noteKey: "dashboard.avgHandlingNote",
				noteTone: "default",
				rows: ranked.map(([id, bucket]) => ({
					key: id,
					label: bucket.name,
					value: bucket.total,
					// The secondary figure is the manager's mean handling time — the
					// column that tells you who is sitting on their queue.
					note: avgDays(bucket),
					tone: id === "unassigned" ? "danger" : bucket.overdue > 0 ? "warning" : "default",
				})),
			},
		],
		lists: [
			{
				key: "managerOverdue",
				unitKey: "dashboard.units.applications",
				items: ranked.map(([id, bucket]) => ({
					key: id,
					label: bucket.name,
					sublabel: `${bucket.total} в работе`,
					// The single worst card is what makes the number actionable.
					reason: bucket.worst,
					value: bucket.overdue,
					tone: bucket.overdue >= 2 ? "danger" : bucket.overdue > 0 ? "warning" : "default",
					href: id === "unassigned" ? "/applications?assignee=none" : `/applications?assignee=${id}`,
				})),
			},
		],
	};
};

export const dashboard: Api["dashboard"] = {
	async overview(range: DashboardRange): Promise<DashboardOverview> {
		await sleep(300);

		// Catalog counts are absolute, not range-scoped: the founder reads them as
		// "what is the state of the catalog right now".
		const published = 10_734;
		const moderation = 900;
		// The number she actually asked for: of what is sitting in moderation, how
		// much has been there longer than a day.
		const staleModeration = 617;

		return {
			range,
			seller: {
				cards: [
					// 1 284 shops ever registered, of which 96 are trading — the funnel
					// block below shows where the rest fell out.
					card("sellers", "/shops", range, 29, 4, 0.8, 1_284),
					card("gmv", "/orders", range, 71, 145_000, 0.6),
					card("orders", "/orders", range, 47, 18, 0.35, 41_868),
				],
				blocks: [
					{
						key: "sellerFunnel",
						href: "/applications",
						rows: [
							{ key: "new", value: 21 },
							{ key: "contacted", value: 17 },
							{ key: "registered", value: 24 },
							{ key: "filling", value: 38 },
							{ key: "moderation_pending", value: 17, tone: "warning" },
							{ key: "moderation_passed", value: 12 },
							{ key: "active", value: 96 },
						],
					},
					{
						key: "catalog",
						href: "/products",
						rows: [
							{ key: "published", value: published },
							{ key: "moderation", value: moderation, note: staleModeration, tone: "warning" },
							{ key: "rejected", value: 421 },
							{ key: "draft", value: 1_268 },
							{ key: "blocked", value: 63 },
							{ key: "archived", value: 734 },
						],
					},
				],
			},
			buyer: {
				cards: [
					// Matches "Всего зарегистрировано" in the audience block — the same
					// number must not appear twice with two values.
					card("buyers", "/users", range, 11, 24, 0.45, 18_402),
					averaged("activeBuyers", "/users", range, 83, 1_940, 0.3),
					averaged("aov", "/orders", range, 17, 4_850, 0.12),
					averaged("purchaseFrequency", "/orders", range, 61, 2, 0.1),
				],
				blocks: [
					{
						key: "audience",
						href: "/users",
						rows: [
							{ key: "registered", value: 18_402 },
							// The segment she called out by name: registered, never bought.
							{ key: "neverPurchased", value: 11_268, tone: "warning" },
							{ key: "repeatBuyers", value: 3_411 },
							{ key: "appInstalls", value: 7_940 },
							{ key: "sessions", value: 42_106 },
							// Minutes — rendered with a unit suffix.
							{ key: "avgSessionMinutes", value: 6 },
						],
					},
					{
						// Attribution: Богдан called this "вторым блоком", so it is
						// flagged secondary and rendered collapsed.
						key: "acquisition",
						secondary: true,
						rows: [
							{ key: "direct", value: 5_120 },
							{ key: "ads", value: 3_870 },
							{ key: "social", value: 2_940 },
							{ key: "search", value: 1_780 },
							{ key: "referral", value: 690 },
						],
					},
				],
			},
			quality: {
				cards: [
					card("returns", "/orders", range, 37, 11, -0.12, 3_419),
					averaged("returnRate", "/orders", range, 91, 3, 0.05),
					averaged("fulfilmentDays", "/orders", range, 53, 4, -0.08),
					// Reviews belong to quality, not to a section of their own —
					// «по сути же отзывы это тоже качество».
					card("reviews", "/products", range, 61, 48, 0.18, 18_402),
					card("negativeReviews", "/products", range, 73, 7, -0.04, 2_611),
				],
				blocks: [
					{
						// The reasons breakdown — what actually drives the return rate.
						key: "returnReasons",
						href: "/orders",
						rows: [
							{ key: "notAsDescribed", value: 118, tone: "danger" },
							{ key: "damaged", value: 94, tone: "danger" },
							{ key: "sizeMismatch", value: 63, tone: "warning" },
							{ key: "qualityBelowExpected", value: 47, tone: "warning" },
							{ key: "changedMind", value: 39 },
							{ key: "lateDelivery", value: 22 },
						],
					},
					{
						// Rating histogram: "10 тысяч отзывов, негативных столько-то,
						// рейтингом таким-то" needs the shape, not just the total.
						key: "reviewRatings",
						href: "/products",
						rows: [
							{ key: "stars5", value: 11_284 },
							{ key: "stars4", value: 3_507 },
							{ key: "stars3", value: 1_000 },
							{ key: "stars2", value: 1_446, tone: "warning" },
							{ key: "stars1", value: 1_165, tone: "danger" },
						],
					},
					{
						// Price-band distribution, straight from the seller panel.
						key: "priceBands",
						href: "/products",
						rows: [
							{ key: "under1k", value: 1_840 },
							{ key: "1kTo5k", value: 4_270 },
							{ key: "5kTo10k", value: 3_115 },
							{ key: "10kTo20k", value: 1_002 },
							{ key: "over20k", value: 507 },
						],
					},
				],
				lists: [
					{
						key: "topReturns",
						unitKey: "dashboard.units.returns",
						items: TOP_RETURNS.map(([label, sublabel, value, reason], i) => ({
							key: `ret_${i}`,
							label,
							sublabel,
							value,
							reason,
							tone: value >= 25 ? "danger" : value >= 15 ? "warning" : "default",
						})),
					},
					{
						key: "topReturnShops",
						unitKey: "dashboard.units.returns",
						items: TOP_RETURN_SHOPS.map(([label, slug, value, rate, reason]) => ({
							key: slug,
							label,
							href: `/shops/${slug}`,
							sublabel: `${rate}% заказов`,
							value,
							reason,
							tone: rate >= 10 ? "danger" : rate >= 7 ? "warning" : "default",
						})),
					},
					{
						key: "topNegativeReviews",
						unitKey: "dashboard.units.reviews",
						items: TOP_REVIEWS_NEGATIVE.map(([label, slug, value, rating]) => ({
							key: slug,
							label,
							href: `/shops/${slug}`,
							sublabel: `рейтинг ${rating.toFixed(1)}`,
							value,
							tone: rating < 3 ? "danger" : "warning",
						})),
					},
					{
						key: "topPositiveReviews",
						unitKey: "dashboard.units.reviews",
						items: TOP_REVIEWS_POSITIVE.map(([label, slug, value, rating]) => ({
							key: slug,
							label,
							href: `/shops/${slug}`,
							sublabel: `рейтинг ${rating.toFixed(1)}`,
							value,
						})),
					},
					{
						key: "topDelays",
						unitKey: "dashboard.units.orders",
						items: TOP_DELAYS.map(([label, slug, value, days]) => ({
							key: slug,
							label,
							// Deep-links to the real shop card, like the finance rows do.
							href: `/shops/${slug}`,
							sublabel: `~${days} дн.`,
							value,
							tone: days >= 7 ? "danger" : days >= 5 ? "warning" : "default",
						})),
					},
				],
			},
			managers: await managerWorkload(),
			applications: await applicationsSummary(),
		};
	},
};
